// src/utils/memberNumber.ts
//
// Přidělení pořadového čísla (users.sequenceNumber) nového člena.
//
// Dřív AuthPage dávala usersSnapshot.size + 1 → po každém smazaném nebo do
// users_backup přesunutém účtu dostal nový střelec číslo, které už někdo má,
// a dvě souběžné registrace totéž číslo. Číslo je součástí variabilního
// symbolu (YY+RRR+SEQ, členství YY+099+SEQ) a klíčem výsledků – duplicita
// rozbije párování plateb. Nově se čísla přidělují z počítadla
// counters/members { last, updatedAt } v transakci (pravidla: jen růst).
//
// Bezpečnost (a): střelec cizí profily nečte, takže registrace bere číslo
// jen z počítadla (createMemberProfile, takeSequenceNumber – profil a posun
// počítadla o +1 v JEDNÉ transakci, jinak je pravidla nepustí). Počítadlo
// zakládá a srovnává s nejvyšším číslem v users správce (Příprava dat,
// securityMigration); registrace bez počítadla se odmítne (lks/counter-missing)
// – dřív dostal první člen „001", tedy číslo (a variabilní symbol) někoho
// jiného. allocateSequenceNumber s legacyMax zůstává pro správce (AdminOverview).

import {
  doc,
  runTransaction,
  serverTimestamp,
  type DocumentData,
  type Transaction,
} from "firebase/firestore";
import { db } from "../firebase";
import { reportError, reportWarning } from "./reportError";

/** Dokument počítadla pořadových čísel. */
const COUNTER_COLLECTION = "counters";
const COUNTER_DOC_ID = "members";

const counterRef = () => doc(db, COUNTER_COLLECTION, COUNTER_DOC_ID);

/**
 * Horní mez smysluplného čísla: víc číslic se do variabilního symbolu
 * (max. 10 číslic) nevejde. Vyšší hodnota v datech je překlep/nesmysl
 * a nesmí počítadlo vystřelit nahoru.
 */
const MAX_SEQUENCE_NUMBER = 99999;

/**
 * Prohraný souběh o počítadlo: dva střelci přečtou totéž last a oba zapíší
 * last + 1. Pravidla zastaralý zápis poraženého odmítnou dřív, než Firestore
 * porovná verzi dokumentu (PERMISSION_DENIED místo FAILED_PRECONDITION –
 * ověřeno v emulátoru) a SDK takovou transakci samo nezopakuje. Celá
 * transakce se proto zkusí znovu (s čerstvým počítadlem) až
 * RACE_RETRIES× s náhodnou prodlevou, teprve pak chyba projde.
 */
const RACE_RETRIES = 5;
const RACE_DELAY_MIN_MS = 30;
const RACE_DELAY_MAX_MS = 150;

/** Kód chyby Firebase / vlastní chyby ("permission-denied", "lks/…"), jinak "". */
const errorCode = (err: unknown): string => {
  const code =
    err && typeof err === "object" ? (err as { code?: unknown }).code : undefined;
  return typeof code === "string" ? code : "";
};

/**
 * Chyba s vlastním kódem: firebaseErrorMessage ji přeloží na hlášku pro
 * uživatele (obyčejná Error by skončila jako obecné „Chyba při registraci").
 */
const codedError = (code: string, message: string): Error & { code: string } => {
  const err = new Error(message) as Error & { code: string };
  err.code = code;
  return err;
};

const pause = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Formát uloženého čísla – beze změny proti dřívějšku: do 999 na 3 místa
 * ("007"), od 1000 na 4 místa ("1000").
 */
export function padSequenceNumber(n: number): string {
  return n <= 999
    ? n.toString().padStart(3, "0")
    : n.toString().padStart(4, "0");
}

/** Číslo z uložené hodnoty (string "007" i number 7); nečíselné/nesmyslné → null. */
export function toSequenceInt(raw: unknown): number | null {
  let n: number;
  if (typeof raw === "number") {
    n = raw;
  } else if (typeof raw === "string" && /^\s*\d+\s*$/.test(raw)) {
    n = parseInt(raw, 10);
  } else {
    return null;
  }
  if (!Number.isFinite(n) || n <= 0 || Math.floor(n) !== n) return null;
  if (n > MAX_SEQUENCE_NUMBER) return null;
  return n;
}

/** True pro uložené číslo, které lze použít (importované záznamy mívají prázdné/„undefined"). */
export function isValidSequenceNumber(raw: unknown): boolean {
  return toSequenceInt(raw) !== null;
}

/**
 * Nejvyšší pořadové číslo mezi dokumenty (např. všechny users).
 * Bere string i number; nečíselné hodnoty ('undefined', 'null', prázdné)
 * a hodnoty nad 99999 ignoruje. Bez platného čísla vrací 0.
 */
export function maxSequenceNumber(
  docs: Array<{ sequenceNumber?: unknown } | null | undefined>
): number {
  let max = 0;
  for (const d of docs) {
    const n = d ? toSequenceInt(d.sequenceNumber) : null;
    if (n !== null && n > max) max = n;
  }
  return max;
}

/**
 * Uvnitř transakce: přečte počítadlo, zapíše last = next a vrátí next.
 *  - legacyMax = číslo (správce – zná čísla všech users): next =
 *    max(last nebo 0, legacyMax) + 1, chybějící počítadlo se založí;
 *  - legacyMax = null (registrace – cizí profily nevidí): next = last + 1
 *    a počítadlo MUSÍ existovat (jinak lks/counter-missing). Bez něj by
 *    registrace začala od „001" – číslo, a tedy i variabilní symbol, už
 *    nejspíš má jiný člen.
 * Poškozené počítadlo (hodnota nad MAX_SEQUENCE_NUMBER) se hlásí jako
 * chyba, ne tiše jako 6místné číslo s nefunkčním variabilním symbolem.
 */
async function bumpCounter(
  tx: Transaction,
  legacyMax: number | null
): Promise<number> {
  const ref = counterRef();
  const snap = await tx.get(ref);
  const raw: unknown = snap.exists() ? snap.get("last") : undefined;
  const stored =
    typeof raw === "number" && Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 0;
  if (legacyMax === null && stored === 0) {
    throw codedError(
      "lks/counter-missing",
      "Počítadlo pořadových čísel (counters/members) chybí – číslo nového člena nejde přidělit."
    );
  }
  const value = Math.max(stored, legacyMax === null ? 0 : legacyMax) + 1;
  if (stored > MAX_SEQUENCE_NUMBER || value > MAX_SEQUENCE_NUMBER) {
    throw codedError(
      "lks/sequence-out-of-range",
      `Počítadlo pořadových čísel je mimo rozsah (${stored}).`
    );
  }
  tx.set(ref, { last: value, updatedAt: serverTimestamp() }, { merge: true });
  return value;
}

/**
 * runTransaction, který po permission-denied celou transakci zopakuje
 * (prohraný souběh o počítadlo – viz RACE_RETRIES): až 5× s náhodnou
 * prodlevou 30–150 ms, pak chybu propustí. Jiné chyby hned. Pro transakce
 * zakládající vlastní profil (registrace) a pro přidělení čísla správcem.
 */
export async function runTransactionRetrying<T>(
  updateFunction: (tx: Transaction) => Promise<T>
): Promise<T> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await runTransaction(db, updateFunction);
    } catch (err) {
      if (errorCode(err) !== "permission-denied" || attempt >= RACE_RETRIES) throw err;
      reportWarning("memberNumber.retry", err, { attempt: attempt + 1 });
      await pause(
        RACE_DELAY_MIN_MS +
          Math.floor(Math.random() * (RACE_DELAY_MAX_MS - RACE_DELAY_MIN_MS + 1))
      );
    }
  }
}

/**
 * Registrace: nové pořadové číslo v transakci volajícího – počítadlo +1
 * (musí existovat, jinak lks/counter-missing), vrátí ho naformátované.
 * Volat po všech tx.get (Firestore: čtení před zápisy). Pravidla posun
 * pustí jen spolu se založením vlastního profilu s tímto číslem.
 */
export async function takeSequenceNumber(tx: Transaction): Promise<string> {
  return padSequenceNumber(await bumpCounter(tx, null));
}

/**
 * Přidělí další pořadové číslo a vrátí ho naformátované (padSequenceNumber).
 * Jen správce (AdminOverview – číslo existujícímu členovi): pravidla
 * ostatním posun počítadla bez zakládání vlastního profilu nepustí.
 *
 * Transakce nad counters/members: next = max(last nebo 0, legacyMax) + 1,
 * zapíše last = next. legacyMax (maxSequenceNumber všech users) chrání
 * před kolizí s čísly přidělenými před zavedením počítadla nebo ručně.
 *
 * Když transakce selže (offline, pravidla), chybu zaloguje a VYHODÍ.
 * Tichý náhradní výpočet by mohl vydat duplicitní číslo.
 */
export async function allocateSequenceNumber(
  legacyMax: number
): Promise<string> {
  const safeLegacyMax =
    Number.isFinite(legacyMax) && legacyMax > 0 ? Math.floor(legacyMax) : 0;

  try {
    const next = await runTransactionRetrying((tx) => bumpCounter(tx, safeLegacyMax));
    return padSequenceNumber(next);
  } catch (err) {
    reportError("allocateSequenceNumber", err, { legacyMax: safeLegacyMax });
    throw err;
  }
}

/**
 * Registrace: založí profil users/{uid} s NOVÝM pořadovým číslem v JEDNÉ
 * transakci – přečte profil (už existuje → nic, vrátí null: registraci
 * mezitím dokončilo jiné zařízení), přečte counters/members, zapíše
 * last + 1 a profil { ...data, sequenceNumber } (číslo z data se přepíše).
 * Pravidla střelci dovolí posunout počítadlo jen o +1 a jen spolu se
 * založením vlastního profilu s tímto číslem. Bez legacyMax – cizí profily
 * střelec nečte; chybějící počítadlo = chyba lks/counter-missing.
 * Prohraný souběh o počítadlo se zopakuje (runTransactionRetrying).
 *
 * alsoInTransaction: další zápisy do téže transakce (smazání rozpracované
 * registrace pendingProfiles – utils/completeRegistration).
 *
 * Vrací přidělené číslo, null = profil už existoval (nic se nezapsalo).
 * Selhání zaloguje a VYHODÍ – nezapíše se nic a jde to zopakovat.
 */
export async function createMemberProfile(
  uid: string,
  data: DocumentData,
  alsoInTransaction?: (tx: Transaction) => void
): Promise<string | null> {
  const profileRef = doc(db, "users", uid);
  try {
    return await runTransactionRetrying(async (tx) => {
      const existing = await tx.get(profileRef);
      if (existing.exists()) return null;
      const sequenceNumber = await takeSequenceNumber(tx);
      tx.set(profileRef, { ...data, sequenceNumber });
      if (alsoInTransaction) alsoInTransaction(tx);
      return sequenceNumber;
    });
  } catch (err) {
    reportError("createMemberProfile", err, { uid });
    throw err;
  }
}
